import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TIME_TRACKING_TIME_FORMAT } from '@app/constants';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { DeadlineReminder } from '@app/modules/time-tracking/interfaces/deadline-reminders.interface';
import { Settings, TimeTrackingMethod } from '@app/modules/time-tracking/interfaces/settings.interface';
import { Days } from '@app/modules/time-tracking/meta/time-tracking-days';
import { TimeTrackingSettingsService } from '@app/modules/time-tracking/services/time-tracking-settings.service';
import { AnalyticService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Locale } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

type ReminderKeys =
    | 'remindersApproversDeadline1'
    | 'remindersApproversDeadline2'
    | 'remindersApproversDeadline3'
    | 'remindersEmployeesDeadline1'
    | 'remindersEmployeesDeadline2'
    | 'remindersEmployeesDeadline3';

@Component({
    templateUrl: './general-settings.template.html',
    styleUrls: ['./general-settings.style.scss'],
})
export class TimeTrackingGeneralSettingsView implements OnInit {
    @ViewChild('form', { static: true }) form!: NgForm;
    isLoading = false;
    settings: Settings = {
        remindersWeeklyDeadlineDayOfWeek: null,
    };
    selectedDisplayTime = '';
    timeOptions: { value: string; display: string }[];

    days = Days;
    numberOfDays: number[] = [1, 2, 3, 4, 5, 6];

    approverReminders: DeadlineReminder[] = [];
    employeeReminders: DeadlineReminder[] = [];

    timeZone = 'ET';

    hasTimeClockFlag = false;
    hasTimerClockFlag = false;

    // This will be set by the user and then all other reminders all use this as an anchor
    anchorDate = moment();

    currentLocale: Locale;

    constructor(
        public readonly timeTrackingSettingsService: TimeTrackingSettingsService,
        private notifyService: NotifyService,
        private featureService: FeatureService,
        private location: Location,
        private analyticsService: AnalyticService,
        private translateService: TranslateService
    ) {
        this.timeOptions = [];
        this.currentLocale = translateService.currentLang as Locale;
        this.timeOptions = Array.from(Array(24)).map((_, index) => {
            const time = moment().set('hour', index).set('minute', 0).set('second', 0);
            return {
                value: time.format('HH:mm'),
                display: time.format(TIME_TRACKING_TIME_FORMAT[this.currentLocale]),
            };
        });
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this.hasTimeClockFlag = await this.featureService.has(FeatureFlag.timeClock);
        this.hasTimerClockFlag = await this.featureService.has(FeatureFlag.timeClockTimer);

        try {
            this.settings = await this.timeTrackingSettingsService.get();

            delete this.settings.counts;
            this.settings.remindersWeeklyDeadlineDayOfWeek = this.settings.remindersWeeklyDeadlineDayOfWeek || 'friday';
            this.settings.remindersWeeklyDeadlineTimeOfDay = this.settings.remindersWeeklyDeadlineTimeOfDay || '17:00';
            this.selectedDisplayTime =
                moment(this.settings.remindersWeeklyDeadlineTimeOfDay, 'HH:mm').format(
                    TIME_TRACKING_TIME_FORMAT[this.currentLocale]
                ) +
                ' ' +
                this.translateService.instant(`time-tracking.generalSettingsPage.timeZones.${this.timeZone}`);

            // Create a moment object with the day of the weekly reminders
            this.anchorDate = moment().day(this.settings.remindersWeeklyDeadlineDayOfWeek);

            this.loadReminders();

            if (!this.approverReminders.length) {
                this.approverReminders = [
                    {
                        fieldId: 1,
                        deadline: 1,
                        dayOfWeek: this.anchorDate.clone().subtract(1, 'days').format('dddd').toLowerCase(),
                    },
                ];
            }

            if (!this.employeeReminders.length) {
                this.employeeReminders = [
                    {
                        fieldId: 1,
                        deadline: 1,
                        dayOfWeek: this.anchorDate.clone().subtract(1, 'days').format('dddd').toLowerCase(),
                    },
                ];
            }
            // set the initial values for the properties
            this.setRemindersEmployeesDeadline();
            this.setRemindersApproversDeadline();
            if (!this.settings.futureEntriesAllowedDays) {
                // if there's no allowed days set, we use 30 as a reasonable default
                // this makes for a nicer UI experience
                this.settings.futureEntriesAllowedDays = 30;
            }
        } catch (err) {
            this.notifyService.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    loadReminders(): void {
        [1, 2, 3].forEach((i) => {
            /*
             * The idea here is to use a union of the exact property names we are trying to access.
             * Helps with Typescript not complaining for type safety
             */
            const approverDeadlineProp = `remindersApproversDeadline${i}` as ReminderKeys;
            const employeeDeadlineProp = `remindersEmployeesDeadline${i}` as ReminderKeys;

            if (this.settings[approverDeadlineProp]) {
                this.approverReminders.push({
                    fieldId: i,
                    deadline: +this.settings[approverDeadlineProp],
                    dayOfWeek: this.anchorDate
                        .clone()
                        .subtract(+this.settings[approverDeadlineProp], 'days')
                        .format('dddd')
                        .toLowerCase(),
                });
            }

            if (this.settings[employeeDeadlineProp]) {
                this.employeeReminders.push({
                    fieldId: i,
                    deadline: +this.settings[employeeDeadlineProp],
                    dayOfWeek: this.anchorDate
                        .clone()
                        .subtract(+this.settings[employeeDeadlineProp], 'days')
                        .format('dddd')
                        .toLowerCase(),
                });
            }
        });
    }

    addApprovalReminder(): void {
        const nextReminderNumber = this.approverReminders.length + 1;
        // This give us the next deadline day from 1 to 6
        let nextReminderDeadline = (this.approverReminders[this.approverReminders.length - 1].deadline + 1) % 6;

        // This case is need since when first reminder is selected as 1 and the third is added it can overlap with the firsts number of days
        if (this.findNextFreeDayValue(this.approverReminders, nextReminderDeadline)) {
            nextReminderDeadline = nextReminderDeadline + 1;
        }

        if (nextReminderDeadline === 0) {
            nextReminderDeadline = 6;
        }

        this.approverReminders.push({
            fieldId: nextReminderNumber,
            deadline: nextReminderDeadline,
            dayOfWeek: this.anchorDate.clone().subtract(nextReminderDeadline, 'days').format('dddd').toLowerCase(),
        });

        this.setRemindersApproversDeadline();
    }

    addEmployeeReminder(): void {
        const nextReminderNumber = this.employeeReminders.length + 1;
        let nextReminderDeadline = (this.employeeReminders[this.employeeReminders.length - 1].deadline + 1) % 6;

        if (this.findNextFreeDayValue(this.employeeReminders, nextReminderDeadline)) {
            nextReminderDeadline = nextReminderDeadline + 1;
        }

        if (nextReminderDeadline === 0) {
            nextReminderDeadline = 6;
        }

        this.employeeReminders.push({
            fieldId: nextReminderNumber,
            deadline: nextReminderDeadline,
            dayOfWeek: this.anchorDate.clone().subtract(nextReminderDeadline, 'days').format('dddd').toLowerCase(),
        });

        this.setRemindersEmployeesDeadline();
    }

    isDaySelected(selectedDay: number, reminders: DeadlineReminder[], currentIndex: number): boolean {
        const isAlreadySelected = reminders.some((reminder, index) => {
            return reminder.deadline === selectedDay && index !== currentIndex;
        });

        return isAlreadySelected;
    }

    // This is only used in a rare case when a user adds a reminder and it overlaps with an existing reminder
    findNextFreeDayValue(reminders: DeadlineReminder[], nextReminderDeadline: number): boolean {
        return reminders.some((reminder) => {
            return reminder.deadline === nextReminderDeadline;
        });
    }

    onDaySelectionChange(dayOfWeek: string): void {
        this.anchorDate = moment().day(dayOfWeek);

        this.approverReminders.forEach((approverReminder) => {
            approverReminder.dayOfWeek = this.anchorDate
                .clone()
                .subtract(approverReminder.deadline, 'days')
                .format('dddd')
                .toLowerCase();
        });

        this.employeeReminders.forEach((employeeReminder) => {
            employeeReminder.dayOfWeek = this.anchorDate
                .clone()
                .subtract(employeeReminder.deadline, 'days')
                .format('dddd')
                .toLowerCase();
        });
    }

    updateApproverReminderDeadline(
        reminder: { number: number; deadline: number | null | undefined; dayOfWeek: string },
        selectedNumberOfDays: number
    ): void {
        reminder.deadline = selectedNumberOfDays;
        reminder.dayOfWeek = this.anchorDate
            .clone()
            .subtract(selectedNumberOfDays, 'days')
            .format('dddd')
            .toLowerCase();
        this.setRemindersApproversDeadline();
    }

    updateEmployeeReminderDeadline(
        reminder: { number: number; deadline: number | null | undefined; dayOfWeek: string },
        selectedNumberOfDays: number
    ): void {
        reminder.deadline = selectedNumberOfDays;
        reminder.dayOfWeek = this.anchorDate
            .clone()
            .subtract(selectedNumberOfDays, 'days')
            .format('dddd')
            .toLowerCase();
        this.setRemindersEmployeesDeadline();
    }

    // This sets the proper value to each property unfortunately need to dupe this code for the employee reminders too
    setRemindersApproversDeadline(): void {
        for (const reminder of this.approverReminders) {
            const { fieldId, deadline } = reminder;
            if (fieldId === 1) {
                this.settings.remindersApproversDeadline1 = deadline;
            } else if (fieldId === 2) {
                this.settings.remindersApproversDeadline2 = deadline;
            } else if (fieldId === 3) {
                this.settings.remindersApproversDeadline3 = deadline;
            }
        }
    }

    setRemindersEmployeesDeadline(): void {
        for (const reminder of this.employeeReminders) {
            const { fieldId, deadline } = reminder;
            if (fieldId === 1) {
                this.settings.remindersEmployeesDeadline1 = deadline;
            } else if (fieldId === 2) {
                this.settings.remindersEmployeesDeadline2 = deadline;
            } else if (fieldId === 3) {
                this.settings.remindersEmployeesDeadline3 = deadline;
            }
        }
    }

    clearRemindersApproverDeadline(): void {
        this.settings.remindersApproversDeadline1 = null;
        this.settings.remindersApproversDeadline2 = null;
        this.settings.remindersApproversDeadline3 = null;
    }

    clearRemindersEmployeesDeadline(): void {
        this.settings.remindersEmployeesDeadline1 = null;
        this.settings.remindersEmployeesDeadline2 = null;
        this.settings.remindersEmployeesDeadline3 = null;
    }

    removeApproverReminder(index: number): void {
        this.approverReminders.splice(index, 1);
        this.refreshReminderIds(this.approverReminders);
        this.clearRemindersApproverDeadline();
        this.setRemindersApproversDeadline();
    }

    removeEmployeeReminder(index: number): void {
        this.employeeReminders.splice(index, 1);
        this.refreshReminderIds(this.employeeReminders);
        this.clearRemindersEmployeesDeadline();
        this.setRemindersEmployeesDeadline();
    }

    refreshReminderIds(reminders: DeadlineReminder[]): void {
        reminders.forEach((reminder, index) => {
            reminder.fieldId = index + 1;
        });
    }

    async onSubmit(): Promise<void> {
        if (!this.form.valid || this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.sendAnalytics();

        if (!this.settings.remindersWeeklyDeadline) {
            try {
                this.settings.remindersWeeklyDeadline = false;
                this.clearPropertiesSettings();
                await this.timeTrackingSettingsService.set(this.settings);
                this.notifyService.success('time-tracking.generalSettingsPage.successMessage');
                this.location.back();
            } catch (err) {
                this.notifyService.error(err);
                this.isLoading = false;
            }

            return;
        }

        try {
            await this.timeTrackingSettingsService.set(this.settings);
            this.notifyService.success('time-tracking.generalSettingsPage.successMessage');
            this.location.back();
        } catch (err) {
            this.notifyService.error(err);
            this.isLoading = false;
        }
    }

    onCancel(): void {
        this.location.back();
    }

    clearPropertiesSettings(): void {
        this.settings.remindersWeeklyDeadlineDayOfWeek = null;
        this.settings.remindersWeeklyDeadlineTimeOfDay = null;
        this.settings.remindersApproversDeadline1 = null;
        this.settings.remindersApproversDeadline2 = null;
        this.settings.remindersApproversDeadline3 = null;
        this.settings.remindersEmployeesDeadline1 = null;
        this.settings.remindersEmployeesDeadline2 = null;
        this.settings.remindersEmployeesDeadline3 = null;
    }

    onStartAndEndTimeToggle(newValue: TimeTrackingMethod): void {
        let eventName = AnalyticEvents.TimeTrackingGeneralSettingsTurnOffStartAndEndTime;

        if (newValue === 'start_end_time') {
            eventName = AnalyticEvents.TimeTrackingGeneralSettingsTurnOnStartAndEndTime;
            return;
        }

        this.settings.allowTimeClockRounding = false;
        this.settings.timeClockRoundingMode = null;
        this.settings.timeClockRoundingMinutes = null;
        this.analyticsService.trackEvent(eventName);
    }

    setDefaultRoundingValues(): void {
        this.settings.timeClockRoundingMode = this.settings.timeClockRoundingMode ?? 'nearest';
        this.settings.timeClockRoundingMinutes = this.settings.timeClockRoundingMinutes ?? 15;
    }

    private async sendAnalytics(): Promise<void> {
        const previousSettings = await this.timeTrackingSettingsService.get();
        const hasToggledFutureTimeTrackingSetting =
            previousSettings.allowFutureEntries !== this.settings.allowFutureEntries;

        if (this.settings.allowTimeClockRounding) {
            // Replace the placeholders with actual values
            const eventName = AnalyticEvents.TimeTrackingTimeClockMetadata.replace(
                ':rounding-type',
                this.settings.timeClockRoundingMode || 'none'
            ).replace(':rounding-increment', String(this.settings.timeClockRoundingMinutes || 0));

            this.analyticsService.trackEvent(eventName as AnalyticEvents);
        }

        if (!hasToggledFutureTimeTrackingSetting) {
            return;
        }

        this.analyticsService.trackEvent(
            this.settings
                ? AnalyticEvents.TimeTrackingGeneralSettingsEnableFutureTimeTracking
                : AnalyticEvents.TimeTrackingGeneralSettingsDisableFutureTimeTracking
        );
    }
}
